import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/config";
import { useAuth } from "@/Admin";

import { ContactForm } from "./ContactForm";
import { ConsentSection } from "./DamageProtectionCard";
import { SafetyAcknowledgments } from "./SafetyAcknowledgments";
import { ProtectionAddOnsCard } from "./ProtectionAddOnsCard";
import { EnchantedCareModal, EnchantedPomModal } from "./ProtectionAddOnsModal";
import { PromoCodeSection } from "./PromoCodeSection";
import { TipSection } from "./TipSection";
import { PaymentForm } from "./PaymentForm";
import { TermsModal } from "./TermsModal";

/**
 * CheckoutForm — orchestrator for the checkout left column.
 * Owns the contact / add-ons / consent / promo state and composes each
 * focused sub-section. Payment lives at the bottom in `PaymentForm`.
 * Must live inside a Stripe <Elements> provider (parent handles that).
 */
export const CheckoutForm = ({ reservationId, products, quote, setQuote, trip, zones, settings, resumeCustomer, gateContact }) => {
  const { user } = useAuth();

  // -------- Contact --------
  // Priority: resumeCustomer (phone-booking / pay-link) → gateContact → logged-in user → empty
  const initialCustomer = resumeCustomer || {};
  const gateName = gateContact ? `${gateContact.firstName || ""} ${gateContact.lastName || ""}`.trim() : "";
  const [firstName, setFirstName] = useState(
    () => (initialCustomer.name || "").split(" ")[0] || gateContact?.firstName || ""
  );
  const [lastName, setLastName] = useState(
    () => (initialCustomer.name || "").split(" ").slice(1).join(" ") || gateContact?.lastName || ""
  );
  const [email, setEmail] = useState(initialCustomer.email || gateContact?.email || "");
  const [phone, setPhone] = useState(initialCustomer.phone || gateContact?.phone || "");

  // -------- Auth strip --------
  const [authMode, setAuthMode] = useState(resumeCustomer ? "guest" : "guest");
  const [authChoice, setAuthChoice] = useState(resumeCustomer ? "guest" : null);

  // -------- Protection add-ons --------
  // Server initializes both to true on /checkout/initiate; we drive the
  // toggles from the freshest quote (survives refreshes / resume links).
  const careChecked = Number(quote?.enchanted_care_fee || 0) > 0;
  const pomChecked = Number(quote?.enchanted_pom_fee || 0) > 0;
  const pomEligible = !!quote?.pom_eligible;
  const pomMinDaysOut = Number(settings?.enchanted_pom_min_days_out ?? 7);
  const [showCareModal, setShowCareModal] = useState(false);
  const [showPomModal, setShowPomModal] = useState(false);

  // -------- Terms + SMS --------
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [smsOptedIn, setSmsOptedIn] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  // -------- Conditional safety acknowledgments (server-defined wording) --------
  const [agreements, setAgreements] = useState(null);
  const [ackAccepted, setAckAccepted] = useState({});
  useEffect(() => { axios.get(`${API}/agreements`).then((r) => setAgreements(r.data)).catch(() => {}); }, []);
  const requiredAcks = useMemo(() => {
    const byId = Object.fromEntries((products || []).map((p) => [p.id, p]));
    const flags = { sleep: "requires_sleep_acknowledgment", mobility: "requires_mobility_acknowledgment" };
    const triggered = new Set();
    (quote?.line_items || []).forEach((li) => {
      const p = byId[li.product_id];
      const pool = [p, ...((li.bundle_components || p?.components || []).map((c) => byId[c.product_id]))].filter(Boolean);
      Object.entries(flags).forEach(([key, f]) => { if (pool.some((x) => x[f])) triggered.add(key); });
    });
    return (agreements?.acknowledgments || []).filter((a) => triggered.has(a.key));
  }, [quote?.line_items, products, agreements]);

  const [submitting, setSubmitting] = useState(false);

  // Pre-fill contact from signed-in customer.
  useEffect(() => {
    if (user && user.role === "customer") {
      const fn = user.first_name || (user.name ? user.name.split(" ")[0] : "");
      const ln = user.last_name || (user.name ? user.name.split(" ").slice(1).join(" ") : "");
      if (fn && !firstName) setFirstName(fn);
      if (ln && !lastName) setLastName(ln);
      if (user.email && !email) setEmail(user.email);
      if (user.phone && !phone) setPhone(user.phone);
      setAuthChoice("signed_in");
    }
    // eslint-disable-next-line
  }, [user]);

  // Toggle helpers → push to backend so PaymentIntent amount updates.
  const careBusy = useRef(false);
  const toggleCare = async (next) => {
    if (careBusy.current) return;
    careBusy.current = true;
    try {
      const r = await axios.post(`${API}/checkout/update`, { reservation_id: reservationId, enchanted_care: next });
      setQuote(r.data.quote);
    } catch (e) {
      toast.error("Couldn't update Enchanted Care — please try again.");
    } finally { careBusy.current = false; }
  };

  const pomBusy = useRef(false);
  const togglePom = async (next) => {
    if (pomBusy.current) return;
    pomBusy.current = true;
    try {
      const r = await axios.post(`${API}/checkout/update`, { reservation_id: reservationId, enchanted_pom: next });
      setQuote(r.data.quote);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update Enchanted Peace of Mind.");
    } finally { pomBusy.current = false; }
  };

  // Gate — the friendly list of "please do X" reasons the Pay button is disabled.
  const gateReasons = useMemo(() => {
    const reasons = [];
    if (!firstName || !lastName) reasons.push("add your name");
    if (!email) reasons.push("add email");
    if (!phone || phone.trim().length < 7) reasons.push("add phone number");
    if (!authChoice) reasons.push("choose Sign in / Create account / Guest");
    if (!termsAccepted) reasons.push("accept Terms of Service");
    requiredAcks.forEach((a) => { if (!ackAccepted[a.key]) reasons.push(`accept the ${a.heading}`); });
    return reasons;
  }, [firstName, lastName, email, phone, authChoice, termsAccepted, requiredAcks, ackAccepted]);

  return (
    <div className="space-y-6" data-testid="checkout-form">
      <ContactForm
        firstName={firstName} setFirstName={setFirstName}
        lastName={lastName} setLastName={setLastName}
        email={email} setEmail={setEmail}
        phone={phone} setPhone={setPhone}
        authChoice={authChoice} setAuthChoice={setAuthChoice}
        authMode={authMode} setAuthMode={setAuthMode}
      />

      <ProtectionAddOnsCard
        careFee={quote?.enchanted_care_fee}
        careChecked={careChecked}
        onToggleCare={toggleCare}
        pomFee={quote?.enchanted_pom_fee}
        pomChecked={pomChecked}
        onTogglePom={togglePom}
        pomEligible={pomEligible}
        pomMinDaysOut={pomMinDaysOut}
        onLearnMoreCare={() => setShowCareModal(true)}
        onLearnMorePom={() => setShowPomModal(true)}
      />

      <ConsentSection
        smsOptedIn={smsOptedIn} setSmsOptedIn={setSmsOptedIn}
        termsAccepted={termsAccepted} setTermsAccepted={setTermsAccepted}
        onOpenTerms={() => setShowTerms(true)}
      />

      <SafetyAcknowledgments acks={requiredAcks} accepted={ackAccepted} setAccepted={setAckAccepted} />

      <PromoCodeSection
        reservationId={reservationId}
        email={email}
        quote={quote}
        setQuote={setQuote}
      />

      <TipSection
        reservationId={reservationId}
        quote={quote}
        setQuote={setQuote}
      />

      <PaymentForm
        reservationId={reservationId}
        quote={quote}
        submitting={submitting}
        setSubmitting={setSubmitting}
        firstName={firstName}
        lastName={lastName}
        email={email}
        phone={phone}
        smsOptedIn={smsOptedIn}
        ackAccepted={ackAccepted}
        gateReasons={gateReasons}
      />

      <EnchantedCareModal open={showCareModal} onClose={() => setShowCareModal(false)} careFee={quote?.enchanted_care_fee} />
      <EnchantedPomModal open={showPomModal} onClose={() => setShowPomModal(false)} pomFee={quote?.enchanted_pom_fee} minDaysOut={pomMinDaysOut} />
      <TermsModal open={showTerms} onClose={() => setShowTerms(false)} />
    </div>
  );
};

export default CheckoutForm;
